import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { startOfWeek, startOfMonth, subDays } from 'date-fns';
import { countPostsAdded, MAX_METRICS_DAYS } from '@/utils/analyticsConstants';

interface ProfileMetrics {
  followersChange: number | null;
  postsChange: number | null;
  postsPerWeek: number | null;
  currentWeekPosts: number;
  currentMonthPosts: number;
  hasInsufficientData: boolean;
  dataPointsCount: number;
}

export const useProfileMetrics = (username: string | null) => {
  const [metrics, setMetrics] = useState<ProfileMetrics | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchMetrics = async () => {
      if (!username) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);

        const now = new Date();
        const mondayOfCurrentWeek = startOfWeek(now, { weekStartsOn: 1 });
        const firstDayOfCurrentMonth = startOfMonth(now);

        const { data: rawData, error } = await supabase
          .from('instagram_daily_metrics')
          .select('*')
          .ilike('username', username)
          .order('scrape_date', { ascending: false });

        if (error) throw error;

        if (!rawData || rawData.length === 0) {
          setMetrics(null);
          setIsLoading(false);
          return;
        }

        // Deduplicate by scrape_date (keep first = most recent entry per day)
        // This matches useAnalytics to ensure consistent calculations
        const uniqueByDate = new Map<string, typeof rawData[0]>();
        rawData.forEach(m => {
          if (!uniqueByDate.has(m.scrape_date)) {
            uniqueByDate.set(m.scrape_date, m);
          }
        });
        const metricsData = Array.from(uniqueByDate.values()).slice(0, MAX_METRICS_DAYS);

        const mostRecent = metricsData[0];
        const dataPointsCount = metricsData.length;

        // Last 7 days baseline for followers/posts change display
        // Try exact 7-day baseline first, then fall back to oldest available
        const sevenDaysAgo = subDays(new Date(mostRecent.scrape_date), 7);
        const sevenDaysAgoTime = sevenDaysAgo.getTime();
        const toleranceTime = subDays(sevenDaysAgo, 2).getTime();

        const exact7DayBaseline = metricsData.find(m => {
          const dataTime = new Date(m.scrape_date).getTime();
          return dataTime <= sevenDaysAgoTime && dataTime >= toleranceTime;
        });

        // Fall back to oldest available data point if we have at least 2 points
        // This ensures new users see progress as soon as day 2
        const last7DaysBaseline = exact7DayBaseline
          || (dataPointsCount >= 2 ? metricsData[metricsData.length - 1] : null);

        // Current week baseline (Mon→now) for posting goal tracking only
        const mondayTimestamp = mondayOfCurrentWeek.getTime();
        const weekStartData = metricsData.find(m => {
          const dataTime = new Date(m.scrape_date).getTime();
          return dataTime <= mondayTimestamp && dataTime >= (mondayTimestamp - 2 * 24 * 60 * 60 * 1000);
        });

        // Month baseline
        const monthStartTimestamp = firstDayOfCurrentMonth.getTime();
        const monthStartData = metricsData.find(m => {
          const dataTime = new Date(m.scrape_date).getTime();
          return dataTime <= monthStartTimestamp && dataTime >= (monthStartTimestamp - 2 * 24 * 60 * 60 * 1000);
        });

        let followersChange: number | null = null;
        let postsChange: number | null = null;
        let currentWeekPosts = 0;
        let currentMonthPosts = 0;

        // Chronological order (oldest first) for countPostsAdded
        const chronological = [...metricsData].reverse();

        // Last 7 days changes (for ProfileStatsCard display)
        if (last7DaysBaseline) {
          followersChange = mostRecent.followers_count - last7DaysBaseline.followers_count;

          // Posts: count only additions (positive day-over-day deltas), not net change
          const baselineIndex = chronological.findIndex(m => m.id === last7DaysBaseline.id);
          const periodData = chronological.slice(Math.max(0, baselineIndex));
          postsChange = countPostsAdded(periodData.map(m => m.posts_count));
        }

        // Current week posts (Mon→now, for posting goal tracking)
        if (weekStartData) {
          const weekIndex = chronological.findIndex(m => m.id === weekStartData.id);
          const weekData = chronological.slice(Math.max(0, weekIndex));
          currentWeekPosts = countPostsAdded(weekData.map(m => m.posts_count));
        }

        // Current month posts
        if (monthStartData) {
          const monthIndex = chronological.findIndex(m => m.id === monthStartData.id);
          const monthData = chronological.slice(Math.max(0, monthIndex));
          currentMonthPosts = countPostsAdded(monthData.map(m => m.posts_count));
        }

        // Posts per week average (show as soon as we have 2+ data points)
        let postsPerWeek: number | null = null;
        if (metricsData.length >= 2) {
          const totalAdded = countPostsAdded(chronological.map(m => m.posts_count));
          const daysDiff = Math.max(1,
            (new Date(mostRecent.scrape_date).getTime() - new Date(chronological[0].scrape_date).getTime()) / (1000 * 60 * 60 * 24)
          );
          postsPerWeek = (totalAdded / daysDiff) * 7;
        }

        const hasInsufficientData = dataPointsCount < 2;

        setMetrics({
          followersChange,
          postsChange,
          postsPerWeek,
          currentWeekPosts,
          currentMonthPosts,
          hasInsufficientData,
          dataPointsCount
        });
      } catch (error: unknown) {
        console.error('Error fetching profile metrics:', error);
        setMetrics(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMetrics();
  }, [username]);

  return { metrics, isLoading };
};
